import { OrderGroup } from './OrderGroup';

/**
 * Why a checkout's stock reservation failed, in words (backend #706). The backend sends a code;
 * the prose it used to send instead was English and named a SKU id, and it was shown to the buyer
 * word for word.
 *
 * Not in `error-messages.ts`: the request succeeded, and the failure is a property of the order
 * group rather than an `ApiError`. Not in `OrderValidation.ts` either, although
 * `insufficientStock` is the checkout gate's code with the gate's quantities — the order lost the
 * race between the gate and the stock booking, so the failure surfaced later instead of as a 422.
 * The gate's text sits on the cart line it is about; this one is shown for the whole order, so it
 * has to name the article itself.
 *
 * **A missing or unknown code returns null on purpose**, as in the dictionary: the caller shows its
 * own generic sentence. A backend before #706 sends no code, and a code added after this build
 * degrades to what the buyer saw before rather than to a blank line.
 */
export function reservationFailureText(orderGroup: OrderGroup | null | undefined): string | null {
  switch (orderGroup?.reservationFailureCode) {
    case 'insufficientStock': {
      const requested = orderGroup.reservationFailureRequestedQuantity;
      const available = orderGroup.reservationFailureAvailableQuantity;
      const name = failedArticleName(orderGroup);
      return name
        ? $localize`:@@reservationFailure.insufficientStock:Für „${name}:name:“ war nicht mehr genügend Bestand vorhanden: bestellt ${requested}:requested:, verfügbar ${available}:available:.`
        : $localize`:@@reservationFailure.insufficientStockUnnamed:Für einen Artikel war nicht mehr genügend Bestand vorhanden: bestellt ${requested}:requested:, verfügbar ${available}:available:.`;
    }
    // Inventory never answered and the backend failed the order out. There is no detail for it.
    case 'timedOut':
      return $localize`:@@reservationFailure.timedOut:Der Bestand konnte nicht rechtzeitig bestätigt werden, die Reservierung wurde abgebrochen.`;
    default:
      return null;
  }
}

/**
 * The name of the position whose listing could not be booked, or null when the group does not
 * carry it — then the sentence says "an article" rather than naming the wrong one.
 */
function failedArticleName(orderGroup: OrderGroup): string | null {
  const skuId = orderGroup.reservationFailureSkuId;
  if (!skuId) {
    return null;
  }
  const position = (orderGroup.orders ?? [])
    .flatMap((order) => order.orderPositions ?? [])
    .find((p) => p.skuId === skuId);
  return position?.skuName1 || null;
}
